import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomChangeRequestSplitView from './CustomChangeRequestSplitView';
import ChangeControlActionsIntegration from './components/ChangeControlActionsIntegration';
import ChangeControlCopyDistribution from './components/ChangeControlCopyDistribution';
import ChangeRequestReferenceDocument from './components/ChangeRequestReferenceDocument';

const trackingLabels = getLabels('Change Request');
const trackingBase = createTrackingConfig('Change Request');

const TOPIC_OPTIONS = [
  { value: 'AUTHORITIES', label: '1. Authorities' },
  { value: 'ASSESSMENT_PROCEDURES', label: '2. Assessment procedures' },
  { value: 'PROCEDURES', label: '3. Procedures' },
  { value: 'CALIBRATIONS', label: '4. Calibrations' },
  { value: 'MAINTENANCE', label: '5. Maintenance' },
  { value: 'TRAINING', label: '6. Training' },
  { value: 'SAFETY', label: '7. Safety' },
  { value: 'VALIDATION', label: '8. Validation' },
  { value: 'SUPPLIERS', label: '9. Suppliers' },
  { value: 'NEW_PRODUCT', label: '10. New product' },
  { value: 'NEW_FACILITY', label: '11. New facility' },
  { value: 'DISTRIBUTION', label: '12. Distribution' },
  { value: 'NEW_CUSTOMER', label: '13. New customer' },
  { value: 'QA', label: '14. QA' },
  { value: 'OTHER', label: 'Other' },
];

const CLASSIFICATION_OPTIONS = [
  { value: 'MAJOR', label: 'Major' },
  { value: 'MINOR', label: 'Minor' },
];

const qmsUserFilter = (user) => user?.isQMS === true;

const YES_NO_OPTIONS = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
];

export const changeRequestConfig = {
  entityName: 'ChangeControl',
  entityTypeId: 6,

  api: {
    baseEndpoint: '/change-request',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
    },
    lookupEndpoints: {
      users: '/users',
      roles: '/role',
      departments: '/department',
      changeControlStatuses: '/change-control-status',
      riskAssessments: '/risk-assessment',
    },
    swrInvalidation: {
      mainList: '/change-request',
      fileAttachments: (entityTypeId) =>
        `/file-attachment/entity/${entityTypeId}/*`,
    },
  },

  ui: {
    drawerWidth: '55%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
    showViewButton: true,
    showEditButton: true,
    showDeleteButton: true,
    showDuplicateButton: false,
  },

  customSplitViewPanel: {
    enabled: true,
    component: CustomChangeRequestSplitView,
  },

  features: {
    fileAttachments: true,
    masterEntityTypeId: 6,
    approvalRequests: false,
  },

  displayName: 'Change Request',
  description: 'Change Control form ΕΝ 01-ΓΔ.14/8',
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',

  fieldsToRemove: [
    'id',
    'status',
    'riskAssessment',
    'initiatedBy',
    'qaInvestigationApprovedBy',
    'extensionQaApprovedBy',
    'completionQaApprovedBy',
    'effectiveness3mBy',
    'effectiveness6mBy',
    'createdBy',
    'updatedBy',
    'createdById',
    'updatedById',
    'createdAt',
    'updatedAt',
    'actions',
    'roleApprovals',
    'fileAttachments',
    'fileAttachmentCount',
  ],
  fieldsToKeep: [],

  fields: [
    { name: 'id', label: 'ID', type: 'hidden', table: false, filterable: false },
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: false,
      placeholder: 'Auto: CC-YYYY-NNN',
      table: { header: 'Code', width: '130px', sortable: true },
      filterable: true,
      validation: { maxLength: 50 },
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'changeControlStatuses',
      options: [],
      defaultValue: 2,
      table: {
        header: 'Status',
        width: '110px',
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { required: true, integer: true },
    },
    {
      name: 'changeDescription',
      label: 'Description of Change',
      type: 'textarea',
      required: true,
      rows: 3,
      table: {
        header: 'Description',
        width: '220px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
      validation: { required: true, message: 'Description is required' },
    },
    {
      name: 'changeCause',
      label: 'Cause of Change',
      type: 'textarea',
      required: false,
      rows: 2,
      table: {
        header: 'Cause',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
    },
    {
      name: 'initiatedById',
      label: 'Initiated By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: qmsUserFilter,
      options: [],
      table: {
        header: 'Initiated By',
        width: '140px',
        sortable: true,
        displayField: 'initiatedBy',
        cellRenderer: 'userDisplay',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'initiatedAt',
      label: 'Initiation Date',
      type: 'date',
      required: false,
      table: {
        header: 'Initiated',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'affectedDepartmentIds',
      label: 'Affected Departments',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      table: false,
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'topicCodes',
      label: 'Change Concerns',
      type: 'multiSelect',
      required: false,
      options: TOPIC_OPTIONS,
      table: false,
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'topicOther',
      label: 'Other (details)',
      type: 'text',
      required: false,
      table: false,
      filterable: false,
    },
    {
      name: 'qaInvestigationApprovedById',
      label: 'QA Approval (Investigation)',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: qmsUserFilter,
      options: [],
      table: false,
      validation: { integer: true },
    },
    {
      name: 'qaInvestigationApprovedAt',
      label: 'QA Approval Date',
      type: 'date',
      required: false,
      table: false,
    },
    {
      name: 'classification',
      label: 'Classification',
      type: 'select',
      required: false,
      options: CLASSIFICATION_OPTIONS,
      table: {
        header: 'Class',
        width: '100px',
        sortable: true,
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'riskAssessmentRequired',
      label: 'Risk Assessment Required',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'RA',
        width: '70px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'riskAssessmentId',
      label: 'Risk Assessment (QRM)',
      type: 'select',
      required: false,
      lookupEndpoint: 'riskAssessments',
      labelField: 'code',
      options: [],
      table: {
        header: 'QRM',
        width: '120px',
        sortable: true,
        displayField: 'riskAssessment',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'qrmIdNo',
      label: 'QRM ID No',
      type: 'hidden',
      required: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyToQa',
      label: 'QA',
      type: 'hidden',
      defaultValue: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyToSafety',
      label: 'Safety Officer',
      type: 'hidden',
      defaultValue: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyToCustomer',
      label: 'Customer',
      type: 'hidden',
      defaultValue: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyToWarehouse',
      label: 'Warehouse',
      type: 'hidden',
      defaultValue: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyToCommercial',
      label: 'Commercial',
      type: 'hidden',
      defaultValue: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyToOther',
      label: 'Other',
      type: 'hidden',
      defaultValue: false,
      table: false,
      filterable: false,
    },
    {
      name: 'copyOtherNote',
      label: 'Other details',
      type: 'hidden',
      table: false,
      filterable: false,
    },
    {
      name: 'extensionDate',
      label: 'Extension Date',
      type: 'date',
      table: false,
    },
    {
      name: 'extensionQaApprovedById',
      label: 'Extension QA Approval',
      type: 'select',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: qmsUserFilter,
      options: [],
      table: false,
      validation: { integer: true },
    },
    {
      name: 'extensionQaApprovedAt',
      label: 'Extension QA Date',
      type: 'date',
      table: false,
    },
    {
      name: 'implementationDate',
      label: 'Implementation Date',
      type: 'date',
      table: {
        header: 'Implemented',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'assessmentSummary',
      label: 'Assessment Summary',
      type: 'textarea',
      rows: 3,
      table: false,
    },
    {
      name: 'actionsCompleted',
      label: 'Actions Completed',
      type: 'select',
      options: YES_NO_OPTIONS,
      table: {
        header: 'Actions Done',
        width: '110px',
        sortable: true,
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'actionsCompletedNote',
      label: 'Actions Justification',
      type: 'textarea',
      rows: 2,
      table: false,
    },
    {
      name: 'completionQaApprovedById',
      label: 'Completion QA Approval',
      type: 'select',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: qmsUserFilter,
      options: [],
      table: false,
      validation: { integer: true },
    },
    {
      name: 'completionQaApprovedAt',
      label: 'Completion QA Date',
      type: 'date',
      table: false,
    },
    {
      name: 'effectiveness3mById',
      label: 'Effectiveness 3m — QA',
      type: 'select',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: qmsUserFilter,
      options: [],
      table: false,
      validation: { integer: true },
    },
    {
      name: 'effectiveness3mAt',
      label: 'Effectiveness 3m — Date',
      type: 'date',
      table: false,
    },
    {
      name: 'effectiveness6mById',
      label: 'Effectiveness 6m — QA',
      type: 'select',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      lookupFilter: qmsUserFilter,
      options: [],
      table: false,
      validation: { integer: true },
    },
    {
      name: 'effectiveness6mAt',
      label: 'Effectiveness 6m — Date',
      type: 'date',
      table: false,
    },
    {
      name: 'effectivenessComments',
      label: 'Effectiveness Comments',
      type: 'textarea',
      rows: 2,
      table: false,
    },
    {
      name: 'referenceDocument',
      label: 'Reference Document',
      type: 'hidden',
      table: {
        header: 'Document Reference',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 40,
      },
      filterable: true,
      filterType: 'text',
    },
    {
      name: 'roleApprovals',
      label: 'Role Approvals',
      type: 'hidden',
      defaultValue: [],
      table: false,
    },
    {
      name: 'fileAttachments',
      label: 'File(s)',
      type: 'fileUpload',
      useFormFileUpload2: true,
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024,
      maxFiles: 10,
      table: {
        header: 'File(s)',
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount',
      },
      filterable: false,
    },
  ],

  get tableColumns() {
    return this.fields
      .filter((field) => field.table !== false && field.table)
      .map((field) => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        dateFormat: field.table.dateFormat,
        originalField: field.name,
      }));
  },

  exportFileName: 'qms_change_requests',

  fieldMappings: {
    status: 'status.name',
    initiatedBy: 'initiatedBy.userDisplayName',
    riskAssessment: 'riskAssessment.code',
  },

  formSections: [
    {
      title: 'I. Proposed Change',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'code',
        'statusId',
        'changeDescription',
        'changeCause',
        'initiatedById',
        'initiatedAt',
      ],
      fieldSpans: {
        code: 'md:col-span-4',
        statusId: 'md:col-span-8',
        changeDescription: 'md:col-span-12',
        changeCause: 'md:col-span-12',
        initiatedById: 'md:col-span-6',
        initiatedAt: 'md:col-span-6',
      },
    },
    {
      title: 'II. Investigation',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'affectedDepartmentIds',
        'topicCodes',
        'topicOther',
        'qaInvestigationApprovedById',
        'qaInvestigationApprovedAt',
      ],
      fieldSpans: {
        affectedDepartmentIds: 'md:col-span-12',
        topicCodes: 'md:col-span-12',
        topicOther: 'md:col-span-12',
        qaInvestigationApprovedById: 'md:col-span-6',
        qaInvestigationApprovedAt: 'md:col-span-6',
      },
    },
    {
      title: 'Actions Required',
      icon: '📝',
      layout: 'custom',
      showSectionHeader: true,
      fields: ['actions'],
      customComponent: ChangeControlActionsIntegration,
    },
    {
      title: 'Classification & Approvals',
      icon: '🏷️',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'classification',
        'riskAssessmentRequired',
        'riskAssessmentId',
      ],
      fieldSpans: {
        classification: 'md:col-span-4',
        riskAssessmentRequired: 'md:col-span-4',
        riskAssessmentId: 'md:col-span-4',
      },
    },
    {
      title: 'Copy',
      icon: '📤',
      layout: 'custom',
      showSectionHeader: true,
      fields: [
        'copyToQa',
        'copyToSafety',
        'copyToCustomer',
        'copyToWarehouse',
        'copyToCommercial',
        'copyToOther',
        'copyOtherNote',
      ],
      customComponent: ChangeControlCopyDistribution,
    },
    {
      title: 'III. Completion',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'extensionDate',
        'extensionQaApprovedById',
        'extensionQaApprovedAt',
        'implementationDate',
        'assessmentSummary',
        'actionsCompleted',
        'actionsCompletedNote',
        'completionQaApprovedById',
        'completionQaApprovedAt',
      ],
      fieldSpans: {
        extensionDate: 'md:col-span-4',
        extensionQaApprovedById: 'md:col-span-4',
        extensionQaApprovedAt: 'md:col-span-4',
        implementationDate: 'md:col-span-4',
        assessmentSummary: 'md:col-span-8',
        actionsCompleted: 'md:col-span-4',
        actionsCompletedNote: 'md:col-span-8',
        completionQaApprovedById: 'md:col-span-6',
        completionQaApprovedAt: 'md:col-span-6',
      },
    },
    {
      title: 'IV. Effectiveness Check',
      icon: '📈',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'effectiveness3mById',
        'effectiveness3mAt',
        'effectiveness6mById',
        'effectiveness6mAt',
        'effectivenessComments',
      ],
      fieldSpans: {
        effectiveness3mById: 'md:col-span-6',
        effectiveness3mAt: 'md:col-span-6',
        effectiveness6mById: 'md:col-span-6',
        effectiveness6mAt: 'md:col-span-6',
        effectivenessComments: 'md:col-span-12',
      },
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments'],
    },
    {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      showSectionHeader: true,
      customComponent: ChangeRequestReferenceDocument,
      fields: [],
    },
  ],

  trackingSection: {
    ...trackingBase,
    fields: trackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: trackingLabels.createdDateLabel,
            userEmptyText: trackingLabels.notSpecifiedText,
            dateEmptyText: trackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: trackingLabels.updatedDateLabel,
              userEmptyText: trackingLabels.notModifiedText,
              dateEmptyText: trackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  messages: {
    loading: 'Loading change requests...',
    noData: 'No change requests found. Create your first record to get started.',
    createSuccess: 'Change request created successfully!',
    updateSuccess: 'Change request updated successfully!',
    deleteSuccess: 'Change request deleted successfully!',
    createError: 'Failed to create change request',
    updateError: 'Failed to update change request',
    deleteError: 'Failed to delete change request',
    loadError: 'Failed to load change requests',
    validationError: 'Validation failed',
  },

  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.fileAttachments;
    delete transformed.fileAttachmentCount;
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;
    delete transformed.status;
    delete transformed.riskAssessment;
    delete transformed.initiatedBy;
    delete transformed.qaInvestigationApprovedBy;
    delete transformed.extensionQaApprovedBy;
    delete transformed.completionQaApprovedBy;
    delete transformed.effectiveness3mBy;
    delete transformed.effectiveness6mBy;
    delete transformed.createdBy;
    delete transformed.updatedBy;

    if (transformed.referenceDocument !== undefined) {
      if (
        transformed.referenceDocument === '' ||
        transformed.referenceDocument === null
      ) {
        delete transformed.referenceDocument;
      } else {
        transformed.referenceDocument = String(
          transformed.referenceDocument,
        ).trim();
      }
    }

    if (!Array.isArray(transformed.affectedDepartmentIds)) {
      transformed.affectedDepartmentIds = transformed.affectedDepartmentIds
        ? [transformed.affectedDepartmentIds]
        : [];
    }
    transformed.affectedDepartmentIds = transformed.affectedDepartmentIds
      .map((v) => parseInt(v, 10))
      .filter((n) => !isNaN(n));

    if (!Array.isArray(transformed.topicCodes)) {
      transformed.topicCodes = transformed.topicCodes
        ? [transformed.topicCodes]
        : [];
    }

    if (transformed.roleApprovals == null || transformed.roleApprovals === '') {
      transformed.roleApprovals = [];
    }

    const fkFields = [
      'statusId',
      'initiatedById',
      'qaInvestigationApprovedById',
      'riskAssessmentId',
      'extensionQaApprovedById',
      'completionQaApprovedById',
      'effectiveness3mById',
      'effectiveness6mById',
    ];
    delete transformed.year;
    delete transformed.serial;
    fkFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const n = parseInt(transformed[field], 10);
          transformed[field] = !isNaN(n) ? n : null;
        }
      }
    });

    const dateFields = [
      'initiatedAt',
      'qaInvestigationApprovedAt',
      'extensionDate',
      'extensionQaApprovedAt',
      'implementationDate',
      'completionQaApprovedAt',
      'effectiveness3mAt',
      'effectiveness6mAt',
    ];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const d = new Date(transformed[field]);
          transformed[field] = !isNaN(d.getTime()) ? d : null;
        }
      }
    });

    [
      'riskAssessmentRequired',
      'copyToQa',
      'copyToSafety',
      'copyToCustomer',
      'copyToWarehouse',
      'copyToCommercial',
      'copyToOther',
    ].forEach((field) => {
      if (transformed[field] !== undefined) {
        transformed[field] = Boolean(transformed[field]);
      }
    });

    ['classification', 'actionsCompleted'].forEach((field) => {
      if (transformed[field] === '' || transformed[field] === undefined) {
        transformed[field] = null;
      }
    });

    if (transformed.code === '') delete transformed.code;

    if (Array.isArray(transformed.actions)) {
      transformed.actions = transformed.actions
        .filter((a) => a && String(a.description || '').trim() !== '')
        .map((a, idx) => {
          const type =
            a.responsibleType === 'ROLE' || a.responsibleRoleId
              ? 'ROLE'
              : 'USER';
          return {
            ...(a.id ? { id: a.id } : {}),
            description: String(a.description).trim(),
            responsibleType: type,
            responsibleUserId:
              type === 'USER' &&
              a.responsibleUserId !== '' &&
              a.responsibleUserId != null
                ? parseInt(a.responsibleUserId, 10)
                : null,
            responsibleRoleId:
              type === 'ROLE' &&
              a.responsibleRoleId !== '' &&
              a.responsibleRoleId != null
                ? parseInt(a.responsibleRoleId, 10)
                : null,
            dueDate: a.dueDate || null,
            isCompleted: Boolean(a.isCompleted),
            recordOrder: a.recordOrder ?? idx + 1,
          };
        });
    } else {
      delete transformed.actions;
    }

    return transformed;
  },

  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true,
  },
};

export default changeRequestConfig;
